// Theme pack manifest schema. Validated on load — third-party packs
// (the future theme store) must pass before any asset is touched.

export const SCHEMA_VERSION = 1;

export type SceneKey = 'home' | 'calendar' | 'day-of-school' | 'number';

export type MascotSlot = 'home' | 'mm';

export interface ThemeManifest {
  schemaVersion: typeof SCHEMA_VERSION;
  /** Unique key within a registry. Lowercase, hyphenated, ASCII. */
  key: string;
  /** Human-readable label shown in the picker. */
  name: string;
  /** Pack version (semver-ish). */
  version: string;
  /** Pack author — "classy core" for built-ins. */
  author: string;
  /** Background scenes the pack provides. `home` is required; others are optional. */
  backgrounds: Partial<Record<SceneKey, string>> & { home: string };
  /** Mascot images. `home` is required; `mm` defaults to home if omitted. */
  mascot: { home: string; mm?: string };
  /** Optional palette overrides. Themes are self-contained: any color a
   *  theme author wants to ship should live in `tokens` so it travels
   *  with the theme pack (no app-code change required). Each value is a
   *  hex color and maps 1:1 to a `--token-*` CSS variable at runtime.
   *
   *  Whitelisted v1 fields:
   *  - `accent` — primary brand tint (was `accentOverride` in v0).
   *  - `cardBg` — base color for opaque cards.
   *  - `textPrimary` — body text color.
   *
   *  `accentOverride` is still accepted as a legacy alias for `accent`. */
  tokens?: {
    accent?: string;
    cardBg?: string;
    textPrimary?: string;
    /** @deprecated use `accent` */
    accentOverride?: string;
  };
  /** Optional thumbnail relative path (used by the theme picker). */
  thumbnail?: string;
  /** Reserved for the future theme store. v1 ignores. */
  permissions?: ReadonlyArray<string>;
}

export class InvalidManifestError extends Error {
  constructor(
    public readonly issue:
      | 'unsupported_schema'
      | 'missing_field'
      | 'invalid_key'
      | 'unsafe_path'
      | 'invalid_token_override',
    message: string,
  ) {
    super(message);
    this.name = 'InvalidManifestError';
  }
}

const KEY_RE = /^[a-z][a-z0-9-]{1,40}$/;
const HEX_RE = /^#[0-9a-fA-F]{3,8}$/;

function ensureSafePath(field: string, value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new InvalidManifestError('missing_field', `${field} must be a non-empty string`);
  }
  // Path must be relative and not escape the pack root.
  if (
    value.startsWith('/') ||
    value.startsWith('..') ||
    value.includes('..\\') ||
    value.includes('../') ||
    value.includes('://')
  ) {
    throw new InvalidManifestError(
      'unsafe_path',
      `${field} must be a relative path inside the pack`,
    );
  }
  return value;
}

function ensureString(field: string, value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new InvalidManifestError('missing_field', `${field} must be a non-empty string`);
  }
  return value;
}

export function parseManifest(raw: unknown): ThemeManifest {
  if (!raw || typeof raw !== 'object') {
    throw new InvalidManifestError('missing_field', 'manifest must be an object');
  }
  const m = raw as Record<string, unknown>;

  if (m.schemaVersion !== SCHEMA_VERSION) {
    throw new InvalidManifestError(
      'unsupported_schema',
      `expected schemaVersion=${SCHEMA_VERSION}, got ${String(m.schemaVersion)}`,
    );
  }

  const key = ensureString('key', m.key);
  if (!KEY_RE.test(key)) {
    throw new InvalidManifestError('invalid_key', `key must match ${KEY_RE}`);
  }

  const name = ensureString('name', m.name);
  const version = ensureString('version', m.version);
  const author = ensureString('author', m.author);

  if (!m.backgrounds || typeof m.backgrounds !== 'object') {
    throw new InvalidManifestError('missing_field', 'backgrounds is required');
  }
  const bgIn = m.backgrounds as Record<string, unknown>;
  const homeBg = ensureSafePath('backgrounds.home', bgIn.home);
  const backgrounds: ThemeManifest['backgrounds'] = { home: homeBg };
  for (const slot of ['calendar', 'day-of-school', 'number'] as const) {
    if (bgIn[slot] !== undefined) {
      backgrounds[slot] = ensureSafePath(`backgrounds.${slot}`, bgIn[slot]);
    }
  }

  if (!m.mascot || typeof m.mascot !== 'object') {
    throw new InvalidManifestError('missing_field', 'mascot is required');
  }
  const mIn = m.mascot as Record<string, unknown>;
  const mascot: ThemeManifest['mascot'] = {
    home: ensureSafePath('mascot.home', mIn.home),
  };
  if (mIn.mm !== undefined) {
    mascot.mm = ensureSafePath('mascot.mm', mIn.mm);
  }

  let tokens: ThemeManifest['tokens'];
  if (m.tokens !== undefined) {
    if (!m.tokens || typeof m.tokens !== 'object') {
      throw new InvalidManifestError('invalid_token_override', 'tokens must be an object');
    }
    const tIn = m.tokens as Record<string, unknown>;
    const out: NonNullable<ThemeManifest['tokens']> = {};
    const validateHex = (field: string, value: unknown): string => {
      if (typeof value !== 'string' || !HEX_RE.test(value)) {
        throw new InvalidManifestError(
          'invalid_token_override',
          `tokens.${field} must be a hex color`,
        );
      }
      return value;
    };
    // accent (with legacy accentOverride alias). New `accent` wins if
    // both are set.
    if (tIn.accent !== undefined) out.accent = validateHex('accent', tIn.accent);
    if (tIn.accentOverride !== undefined && out.accent === undefined) {
      out.accent = validateHex('accentOverride', tIn.accentOverride);
    }
    if (tIn.cardBg !== undefined) out.cardBg = validateHex('cardBg', tIn.cardBg);
    if (tIn.textPrimary !== undefined) {
      out.textPrimary = validateHex('textPrimary', tIn.textPrimary);
    }
    tokens = out;
  }

  let thumbnail: string | undefined;
  if (m.thumbnail !== undefined) {
    thumbnail = ensureSafePath('thumbnail', m.thumbnail);
  }

  let permissions: ReadonlyArray<string> | undefined;
  if (Array.isArray(m.permissions)) {
    permissions = m.permissions.filter((p): p is string => typeof p === 'string');
  }

  const manifest: ThemeManifest = {
    schemaVersion: SCHEMA_VERSION,
    key,
    name,
    version,
    author,
    backgrounds,
    mascot,
  };
  if (tokens !== undefined) manifest.tokens = tokens;
  if (thumbnail !== undefined) manifest.thumbnail = thumbnail;
  if (permissions !== undefined) manifest.permissions = permissions;
  return manifest;
}
